import type { IntlShape } from 'react-intl';

import type { ExtractInjectionAPI, getPosHandlerNode } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type {
	Decoration,
	DecorationSource,
	EditorView,
	NodeView,
} from '@atlaskit/editor-prosemirror/view';

import type { CaptionPlugin } from '../captionPluginType';

/**
 * Builds the static DOM structure for the caption node view.
 *
 * figcaption[data-caption][data-media-caption][data-testid="media-caption"]
 *     div.captionView-content-wrap   ← contentDOM (PM editable region)
 *
 * Styles are applied via CSS selectors in EditorContentContainer — no inline styles.
 */
function createCaptionDOM(node: PMNode): {
	contentDOM: HTMLElement;
	dom: HTMLElement;
} {
	const figcaption = document.createElement('figcaption');
	figcaption.setAttribute('data-caption', 'true');
	figcaption.setAttribute('data-media-caption', 'true');
	figcaption.setAttribute('data-testid', 'media-caption');
	if (node.attrs.localId) {
		figcaption.setAttribute('localid', node.attrs.localId);
	}

	// contentDOM — PM writes the editable content here.
	// Must be a direct child of dom (figcaption) so ProseMirror's domAtPos /
	// vertical cursor movement correctly resolves positions inside the caption.
	const contentWrapper = document.createElement('div');
	contentWrapper.className = 'captionView-content-wrap';

	figcaption.appendChild(contentWrapper);

	return { dom: figcaption, contentDOM: contentWrapper };
}

/**
 * Vanilla (React-free) implementation of CaptionNodeView.
 *
 * Replaces SelectionBasedNodeView + CaptionComponent React render.
 * The caption node is a figcaption containing an editable div (contentDOM).
 * When not selected and empty, shows an inline "Add a caption" placeholder.
 */
export class CaptionNodeView implements NodeView {
	dom: HTMLElement;
	contentDOM: HTMLElement;

	private node: PMNode;
	private pluginInjectionApi?: ExtractInjectionAPI<CaptionPlugin>;

	private cleanupEditorDisabledListener?: () => void;

	constructor(
		node: PMNode,
		view: EditorView,
		getPos: getPosHandlerNode,
		pluginInjectionApi: ExtractInjectionAPI<CaptionPlugin> | undefined,
		intl?: IntlShape,
	) {
		this.node = node;
		this.pluginInjectionApi = pluginInjectionApi;

		const { dom, contentDOM } = createCaptionDOM(node);
		this.dom = dom;
		this.contentDOM = contentDOM;

		this.syncContentEditable();
		this.setupEditorDisabledListener();
	}

	// ── ProseMirror NodeView interface ──────────────────────────────────────

	update(
		node: PMNode,
		_decorations: readonly Decoration[],
		_innerDecorations?: DecorationSource,
	): boolean {
		if (node.type !== this.node.type) {
			return false;
		}
		this.node = node;
		return true;
	}

	ignoreMutation(mutation: MutationRecord | { target: Node; type: 'selection' }): boolean {
		return !this.contentDOM.contains(mutation.target) && mutation.type !== 'selection';
	}

	destroy(): void {
		if (this.cleanupEditorDisabledListener) {
			this.cleanupEditorDisabledListener();
			this.cleanupEditorDisabledListener = undefined;
		}
	}

	private get editorDisabled(): boolean {
		return (
			this.pluginInjectionApi?.editorDisabled?.sharedState.currentState()?.editorDisabled ?? false
		);
	}

	private syncContentEditable(): void {
		this.contentDOM.setAttribute('contenteditable', this.editorDisabled ? 'false' : 'true');
	}

	private setupEditorDisabledListener(): void {
		if (!this.pluginInjectionApi?.editorDisabled || this.cleanupEditorDisabledListener) {
			return;
		}
		this.cleanupEditorDisabledListener =
			this.pluginInjectionApi.editorDisabled.sharedState.onChange(() => {
				this.syncContentEditable();
			});
	}
}

/** Factory function for ProseMirror node view registration. */
export function captionNodeView(
	pluginInjectionApi: ExtractInjectionAPI<CaptionPlugin> | undefined,
	intl?: IntlShape,
) {
	return (node: PMNode, view: EditorView, getPos: getPosHandlerNode): CaptionNodeView => {
		return new CaptionNodeView(node, view, getPos, pluginInjectionApi, intl);
	};
}
